<template>
  <Bar
    :chart-data="convertedData"
    :chart-options="mergedOptions"
    :chart-id="chartId"
    :dataset-id-key="datasetIdKey"
    :plugins="convertedPlugins"
    :css-classes="cssClasses"
    :styles="styles"
    :width="width"
    :height="height" />
</template>

<script>
  import { Bar } from 'vue-chartjs'
  import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'

  import { merge, deepCopy } from 'utils/json'

  import { hexToRgba } from 'utils/colorcode'

  ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

  const LOCAL_CHART_OPTIONS = {
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };

  export default {
    name: 'StackedBarChart',
    components: { Bar },
    props: {
      chartData: {
        type: Object,
        default: null,
      },
      chartOptions: {
        type: Object,
        default: null,
      },
      chartId: {
        type: String,
        default: 'stacked-bar-chart',
      },
      datasetIdKey: {
        type: String,
        default: 'label',
      },
      width: {
        type: Number,
        default: 0,
      },
      height: {
        type: Number,
        default: 0,
      },
      cssClasses: {
        default: '',
        type: String,
      },
      styles: {
        type: Object,
        default: () => { },
      },
      plugins: {
        type: Object,
        default: [],
      },
      percentageChart: {
        type: Boolean,
        default: false,
      },
      chartBackgroundVisible: {
        type: Boolean,
        default: false,
      },
      funnelVisible: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      isHorizontalChart() {
        return this.mergedOptions.indexAxis === "y";
      },
    },
    methods: {
      convertData() {
        this.convertedData = deepCopy(this.chartData);
        
        if (this.percentageChart === false) return;

        if (!Object.keys(this.convertedData).some((k) => k === "datasets")) return;

        let totalDataArray = [];
        this.convertedData.datasets.forEach((d) => {
          if (Object.keys(d).some((k) => k === "data")) {
            d.data.forEach((i, index) => {
              totalDataArray[index] ? totalDataArray[index] = totalDataArray[index] + i : totalDataArray.push(i);
            });
          }
        });

        this.convertedData.datasets.forEach((d) => {
          if (Object.keys(d).some((k) => k === "data")) {
            d.data = d.data.map((i, index) => {
              return Number((i / totalDataArray[index] * 100).toFixed(1));
            });
          }
        });
      },
      convertOptions() {
        this.mergedOptions = merge(this.chartOptions, LOCAL_CHART_OPTIONS);

        if (this.percentageChart === false) return;
        if (!this.mergedOptions.scales) this.mergedOptions.scales = {};
        if (!this.mergedOptions.scales.x) this.mergedOptions.scales.x = {};
        this.mergedOptions.scales.x.min = 0;
        this.mergedOptions.scales.x.max = 100;
        if (!this.mergedOptions.scales.y) this.mergedOptions.scales.y = {};
        this.mergedOptions.scales.y.min = 0;
        this.mergedOptions.scales.y.max = 100;
      },
      convertPlugins() {
        this.convertedPlugins = Array.isArray(this.plugins) ? this.plugins : [this.plugins];

        const chartBackground = {
          id: "chartBackground",
          beforeDraw: function (chart) {
            const isHorizontalChart = chart.options?.indexAxis === "y";
            const { ctx, data, chartArea: { width, height, bottom }, legend } = chart;
            ctx.save();

            let hasMaxLengthIndex = 0;
            let maxLength = 0;
            data.datasets.forEach((set, index) => {
              if (set?.visible && set?.data?.length >= maxLength) {
                hasMaxLengthIndex = index;
                maxLength = set?.data?.length;
              }
            });

            let heightArray = [];
            let baseArray = [];
            data.datasets.forEach((set, dsIndex) => {
              chart.getDatasetMeta(dsIndex).data.forEach((data, dIndex) => {
                heightArray[dIndex] ? heightArray[dIndex] = heightArray[dIndex] + data.height : heightArray[dIndex] = data.height;
              });
              baseArray[dsIndex] = chart.getDatasetMeta(dsIndex).data.reduce((prev, current) => {
                return prev.base > current.base ? prev.base : current.base;
              });
            });

            let maxHeight = Math.max(...heightArray);
            let maxBase = Math.max(...baseArray);

            ctx.fillStyle = hexToRgba(__COLOR_CODE.secondary, 0.15);
            ctx.strokeStyle = hexToRgba(__COLOR_CODE.secondary, 0.15);

            chart.getDatasetMeta(hasMaxLengthIndex).data.forEach((d) => {
              ctx.beginPath();

              if (isHorizontalChart) {
                ctx.moveTo(0, d.y + (d.height / 2));
                ctx.lineTo(width, d.y + (d.height / 2));
                ctx.lineTo(width, d.y - (d.height / 2));
                ctx.lineTo(0, d.y - (d.height / 2));
              } else {
                ctx.moveTo(d.x - (d.width / 2), bottom);
                ctx.lineTo(d.x + (d.width / 2), bottom);
                ctx.lineTo(d.x + (d.width / 2), maxBase - maxHeight);
                ctx.lineTo(d.x - (d.width / 2), maxBase - maxHeight);
              }

              ctx.closePath();

              ctx.stroke();
              ctx.fill();
            });

            ctx.restore();
          }
        };

        if (this.chartBackgroundVisible && this.percentageChart) this.convertedPlugins.push(chartBackground);

        const funnelChart = {
          id: "funnelChart",
          beforeDatasetsDraw: function (chart) {
            const isHorizontalChart = chart.options?.indexAxis === "y";

            const { ctx, data } = chart;
            ctx.save();

            for (let dsIndex = 0; dsIndex < data.datasets.length; dsIndex++) {
              let ds = data.datasets[dsIndex];

              if (chart.getDatasetMeta(dsIndex).visible === false) continue;

              for (let dIndex = 0; dIndex < chart.getDatasetMeta(dsIndex).data.length; dIndex++) {

                let d = chart.getDatasetMeta(dsIndex).data[dIndex];
                let nextData = chart.getDatasetMeta(dsIndex).data[dIndex + 1];

                if (!nextData) continue;

                ctx.beginPath();

                ctx.fillStyle = hexToRgba(ds.backgroundColor, 0.15);
                ctx.strokeStyle = hexToRgba(ds.backgroundColor, 0.15);

                if (isHorizontalChart) {
                  ctx.moveTo(d.base, d.y + (d.height / 2));
                  ctx.lineTo(d.base + d.width, d.y + (d.height / 2));
                  ctx.lineTo(nextData.base + nextData.width, nextData.y - (nextData.height / 2));
                  ctx.lineTo(nextData.base, nextData.y - (nextData.height / 2));
                } else {
                  ctx.moveTo(d.x + (d.width / 2), d.base);
                  ctx.lineTo(d.x + (d.width / 2), d.y);
                  ctx.lineTo(nextData.x - (nextData.width / 2), nextData.y);
                  ctx.lineTo(nextData.x - (nextData.width / 2), nextData.base);
                }

                ctx.closePath();

                ctx.stroke();
                ctx.fill();
                ctx.restore();
              };
            };
            ctx.restore();
          },
        };

        if (this.funnelVisible) this.convertedPlugins.push(funnelChart);
      },
      rewriteTooltips() {
        const convertTooltipLabel = (tooltipItem) => {
          const datasetIndex = tooltipItem.datasetIndex;
          const index = tooltipItem.dataIndex;
          const datasetLabel = this.convertedData.datasets[datasetIndex]?.label;
          const originalValue = this.chartData.datasets[datasetIndex]?.data[index];
          const rateValue = this.convertedData.datasets[datasetIndex]?.data[index];

          return `${datasetLabel}: ${rateValue}% (${originalValue})`;
        };

        const convertTooltipTitle = (tooltipItem) => {
          return tooltipItem[0]?.chart?.options?.scales?.title?.display ? tooltipItem[0]?.label : null;
        };

        if (this.percentageBarChart === false) return;
        if (!this.mergedOptions.plugins) this.mergedOptions.plugins = {};
        if (!this.mergedOptions.plugins.tooltip) this.mergedOptions.plugins.tooltip = {};
        if (!this.mergedOptions.plugins.tooltip.callbacks) this.mergedOptions.plugins.tooltip.callbacks = {};
        this.mergedOptions.plugins.tooltip.callbacks.label = convertTooltipLabel;
        this.mergedOptions.plugins.tooltip.callbacks.title = convertTooltipTitle;
      },
    },
    data: () => {
      return {
        convertedData: {},
        mergedOptions: {},
        convertedPlugins: [],
      }
    },
    created() {
      this.convertData();
      this.convertOptions();
      this.convertPlugins();

      this.rewriteTooltips();
    },
    watch: {
      chartData: {
        handler: function () {
          this.convertData();
          this.convertPlugins();
        },
        deep: true,
      },
      chartOptions: {
        handler: function () {
          this.convertOptions();
        },
        deep: true,
      },
    },
  }
</script>
